import React, { useEffect, useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  normalizeAuthState,
  sessionBadgeKind,
  sessionCanConnect,
  sessionCanReauth,
  sessionNeedsAuthAttention,
} from '../lib/session-auth-state';
import {
  getSessions, createSession, requestQR, getQR,
  disconnectSession, startSession, deleteSession,
  providePhone, provideCode, providePassword, resendCode,
  getProxies, setSessionProxy,
} from '../api.js';

const WORKER_STATUS_CONFIG = {
  connected:    { label: 'Подключено',       badge: 'badge-ok',    tip: 'Процесс подключения работает, мессенджер подключён' },
  starting:     { label: 'Запускается…',  badge: 'badge-info',  tip: 'Процесс подключения запускается' },
  disconnected: { label: 'Отключено', badge: 'badge-warn',  tip: 'Процесс подключения работает, но соединение с мессенджером отсутствует' },
  reconnecting: { label: 'Переподключается', badge: 'badge-warn',  tip: 'Выполняется повторное подключение к мессенджеру' },
  banned:       { label: 'Заблокирован',       badge: 'badge-error', tip: 'Аккаунт заблокирован мессенджером' },
  error:        { label: 'Ошибка',        badge: 'badge-error', tip: 'Произошла критическая ошибка процесса подключения' },
};

const AUTH_STATE_CONFIG = {
  wait_phone: {
    label: 'Введите телефон',
    badge: 'badge-warn',
  },
  wait_code: {
    label: 'Введите код',
    badge: 'badge-warn',
  },
  wait_password: {
    label: 'Введите пароль 2FA',
    badge: 'badge-warn',
  },
  wait_email_code: {
    label: 'Введите код из почты',
    badge: 'badge-warn',
  },
  wait_device_confirmation: {
    label: 'Подтвердите вход',
    badge: 'badge-warn',
  },
  wait_pairing_code: {
    label: 'Код подключения готов',
    badge: 'badge-warn',
  },
  wait_qr: {
    label: 'Отсканируйте QR',
    badge: 'badge-warn',
  },
  authorizing: {
    label: 'Авторизация…',
    badge: 'badge-info',
  },
  authorized: {
    label: 'Авторизован',
    badge: 'badge-ok',
  },
  failed: {
    label: 'Ошибка авторизации',
    badge: 'badge-error',
  },
};

const MESSENGER_AUTH_CAPABILITIES = {
  whatsapp: {
    qr: true,
    phoneCode: false,
  },
  telegram: {
    qr: false,
    phoneCode: true,
  },
  max: {
    qr: false,
    phoneCode: true,
  },
};

function messengerAuthCapabilities(type) {
  return (
    MESSENGER_AUTH_CAPABILITIES[type]
    || {
      qr: false,
      phoneCode: false,
    }
  );
}

const AUTH_STATE_TO_REQUEST = {
  wait_phone: 'phone_requested',
  wait_code: 'code_requested',
  wait_password: 'password_requested',
  wait_email_code: 'email_code_requested',
};

function authStageForSession(
  session,
  data = null,
) {
  const ephemeral = (
    data?.auth_request
    || session?.auth_request
  );

  if (ephemeral) {
    return ephemeral;
  }

  return (
    AUTH_STATE_TO_REQUEST[
      normalizeAuthState(
        session?.auth_state
      )
    ]
    || null
  );
}

function WorkerStatusBadge({
  workerStatus,
  authRequest,
  authState,
  sessionStatus,
  needsReauth,
  restarting,
}) {
  const normalizedAuthState =
    normalizeAuthState(authState);

  const durableAuth =
    AUTH_STATE_CONFIG[
      normalizedAuthState
    ];

  // One decision, shared with the buttons. Rendering from a second copy of
  // the rules is how the badge came to say "Введите код" beside a button
  // offering to wipe that very authorization.
  const kind = sessionBadgeKind({
    worker_status: workerStatus,
    auth_request: authRequest,
    auth_state: authState,
    status: sessionStatus,
    needs_reauth: needsReauth,
    restarting,
  });

  if (kind === 'banned') {
    return (
      <span
        className="badge badge-error"
        title="Аккаунт заблокирован мессенджером — требуется повторная авторизация"
      >
        Заблокирован
      </span>
    );
  }

  if (kind === 'needs-reauth') {
    return (
      <span
        className="badge badge-error"
        title="Не удалось подключиться после нескольких попыток — нужна повторная авторизация"
      >
        Ошибка — нужна авторизация
      </span>
    );
  }

  if (kind === 'authorizing') {
    return (
      <span
        className="badge badge-info"
        title="Идёт авторизация — действий не требуется"
      >
        Авторизация…
      </span>
    );
  }

  if (kind === 'needs-input') {
    const labels = {
      phone_requested:
        'Введите телефон',
      code_requested:
        'Введите код',
      password_requested:
        'Введите пароль 2FA',
      email_code_requested:
        'Введите код из почты',
    };

    return (
      <span
        className={`badge ${
          durableAuth?.badge
          || 'badge-warn'
        }`}
        title="Требуется действие для завершения авторизации"
      >
        {
          labels[authRequest]
          || (authRequest ? 'Нужен ввод' : durableAuth?.label)
          || 'Нужен ввод'
        }
      </span>
    );
  }

  // Reality first. worker_status comes from the worker's own Redis heartbeat,
  // so its absence means no process is running for this session — and then a
  // stored auth_state describes a conversation nobody is having. The panel
  // used to render that stale value and show "Авторизация…" for a session
  // whose container did not exist, which sent an operator hunting for a
  // problem in the wrong place.
  if (kind === 'restarting') {
    return (
      <span
        className="badge badge-warn"
        title="Воркер перезапускается после сбоя — подождите"
      >
        Перезапуск…
      </span>
    );
  }

  if (kind === 'stopped') {
    return (
      <span
        className="badge badge-info"
        title={
          normalizedAuthState === 'authorized'
            ? 'Авторизация сохранена, но воркер не запущен — нажмите «Подключить»'
            : 'Воркер не запущен'
        }
      >
        Не запущен
      </span>
    );
  }

  const key = String(
    workerStatus
  ).trim().toLowerCase();

  const cfg = (
    WORKER_STATUS_CONFIG[key]
    || {
      label:
        workerStatusLabel(
          workerStatus
        ),
      badge: 'badge-info',
      tip: '',
    }
  );

  return (
    <span
      className={`badge ${cfg.badge}`}
      title={cfg.tip}
    >
      {cfg.label}
    </span>
  );
}

function messengerTypeLabel(value) {
  const labels = {
    telegram: 'Telegram',
    max: 'MAX',
    whatsapp: 'WhatsApp',
  };

  return labels[value] || value;
}

function workerStatusLabel(value) {
  const key = String(value || '')
    .trim()
    .toLowerCase();

  const labels = {
    online: 'Подключено',
    connected: 'Подключено',
    healthy: 'Подключено',
    running: 'Подключено',
    starting: 'Запускается…',
    connecting: 'Подключается…',
    reconnecting: 'Переподключается…',
    offline: 'Отключено',
    disconnected: 'Отключено',
    failed: 'Ошибка',
    error: 'Ошибка',
  };

  return labels[key] || value || 'Нет данных';
}


export default function Sessions() {
  const [sessions, setSessions] = useState([]);
  const [proxies, setProxies] = useState([]);
  const [loading, setLoading] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ phone_number: '', messenger_type: 'telegram' });
  const [createResult, setCreateResult] = useState(null);
  const [qrSession, setQrSession] = useState(null);   // session object whose QR modal is open
  const [qrData, setQrData] = useState(null);         // { qr, pairing_code, auth_request }
  const qrPollRef = useRef(null);

  const load = async () => {
    try {
      const sessionRows =
        await getSessions();

      setSessions(
        sessionRows
      );
    } catch {
      // Keep the last successfully loaded session list.
      // An independent auxiliary API must never erase it.
    }
  };

  const loadProxies = async () => {
    try {
      const proxyRows =
        await getProxies();

      setProxies(
        proxyRows
      );
    } catch {
      // Proxy management is not enabled for every tenant yet.
      // Personal sessions remain fully usable with direct connection.
      setProxies([]);
    }
  };

  useEffect(() => {
    load();
    loadProxies();

    const timer =
      setInterval(
        load,
        5000,
      );

    return () =>
      clearInterval(timer);
  }, []);

  useEffect(() => {
    const storageKey =
      'svyazka:pending-personal-auth';

    const openPendingSession = (
      candidate
    ) => {
      if (
        !candidate
        || !candidate.id
      ) {
        return;
      }

      setQrData(null);
      setQrSession(candidate);

      try {
        window.sessionStorage.removeItem(
          storageKey
        );
      } catch {
        // Session storage may be unavailable in a restricted embed.
      }
    };

    try {
      const raw =
        window.sessionStorage.getItem(
          storageKey
        );

      if (raw) {
        openPendingSession(
          JSON.parse(raw)
        );
      }
    } catch {
      // Invalid or unavailable storage must not break account management.
    }

    const handlePendingSession = (
      event
    ) => {
      openPendingSession(
        event?.detail
      );
    };

    window.addEventListener(
      'svyazka:personal-auth-open',
      handlePendingSession,
    );

    return () => {
      window.removeEventListener(
        'svyazka:personal-auth-open',
        handlePendingSession,
      );
    };
  }, []);

  // ── Live personal-auth lifecycle ──
  //
  // One sequential control-plane refresh owns the modal.
  // It follows the session ID only, therefore ordinary list
  // refreshes cannot destroy/recreate the auth polling loop.
  useEffect(() => {
    const sessionId =
      qrSession?.id;

    if (!sessionId) {
      setQrData(null);
      return undefined;
    }

    let cancelled = false;
    let timer = null;

    const poll = async () => {
      try {
        const [
          authData,
          sessionRows,
        ] = await Promise.all([
          getQR(sessionId),
          getSessions(),
        ]);

        if (cancelled) {
          return;
        }

        setQrData(authData);
        setSessions(sessionRows);

        const fresh =
          sessionRows.find(
            (item) =>
              item.id === sessionId
          );

        if (
          fresh?.worker_status === 'connected'
          || normalizeAuthState(
            fresh?.auth_state
          ) === 'authorized'
        ) {
          setQrSession(null);
          return;
        }
      } catch (err) {
        if (!cancelled) {
          console.warn(
            'Personal auth refresh failed',
            err,
          );
        }
      }

      if (!cancelled) {
        timer = window.setTimeout(
          poll,
          750,
        );
      }
    };

    poll();

    return () => {
      cancelled = true;

      if (timer !== null) {
        window.clearTimeout(timer);
      }
    };
  }, [qrSession?.id]);

  const handleQR = async (session) => {
    setLoading((previous) => ({
      ...previous,
      [session.id]: true,
    }));

    setQrData(null);
    setQrSession(session);

    try {
      await requestQR(session.id);
    } catch (err) {
      alert(
        'Не удалось открыть авторизацию: '
        + (
          err?.message
          || String(err)
        )
      );
    } finally {
      setLoading((previous) => ({
        ...previous,
        [session.id]: false,
      }));
    }
  };

  const handleReauth = async (session) => {
    if (
      session.messenger_type === "whatsapp"
    ) {
      return handleQR(session);
    }

    setLoading((previous) => ({
      ...previous,
      [session.id]: true,
    }));

    setQrData(null);
    setQrSession(session);

    try {
      await startSession(
        session.id,
        { force: true },
      );
    } catch (err) {
      alert(
        'Не удалось перезапустить авторизацию: '
        + err.message
      );
    } finally {
      setLoading((previous) => ({
        ...previous,
        [session.id]: false,
      }));

      load();
    }

    return undefined;
  };

  const handleRestart = async (session) => {
    setLoading((previous) => ({
      ...previous,
      [session.id]: true,
    }));

    try {
      await startSession(
        session.id,
        {
          force: true,
        },
      );

      // A restored durable session will reconnect automatically.
      // If the new generation needs operator input, the same live
      // auth window will advance through phone/code/2FA.
      setQrData(null);
      setQrSession(session);

      await load();
    } catch (err) {
      setQrSession(null);

      alert(
        'Не удалось перезапустить аккаунт: '
        + (
          err?.message
          || String(err)
        )
      );
    } finally {
      setLoading((previous) => ({
        ...previous,
        [session.id]: false,
      }));
    }
  };

  const handleDisconnect = async (id) => {
    setLoading((previous) => ({
      ...previous,
      [id]: true,
    }));

    try {
      await disconnectSession(id);
      await load();
    } catch (err) {
      alert(
        'Не удалось отключить аккаунт: '
        + (
          err?.message
          || String(err)
        )
      );
    } finally {
      setLoading((previous) => ({
        ...previous,
        [id]: false,
      }));
    }
  };

  const handleConnect = async (id) => {
    setLoading((previous) => ({
      ...previous,
      [id]: true,
    }));

    const current =
      sessions.find(
        (item) =>
          item.id === id
      );

    try {
      const isOfflineButContainerExists =
        current
        && current.status !== 'connected'
        && current.status !== 'starting';

      let result =
        await startSession(id);

      if (
        result?.message === 'Already running'
        && isOfflineButContainerExists
      ) {
        result =
          await startSession(
            id,
            {
              force: true,
            },
          );
      }

      // Open the control-plane window immediately.
      // The live poll will move it automatically through
      // phone -> code -> password -> connected.
      if (current) {
        setQrData(null);
        setQrSession(current);
      }

      await load();
    } catch (err) {
      alert(
        'Не удалось подключить аккаунт: '
        + (
          err?.message
          || String(err)
        )
      );
    } finally {
      setLoading((previous) => ({
        ...previous,
        [id]: false,
      }));
    }
  };

  // Account removal is intentionally separate from chat deletion.
  // It removes credentials/runtime but keeps the archived channel and history.
  const [pendingDelete, setPendingDelete] = useState(null);

  const handleDelete = async (id) => {
    setPendingDelete(null);

    try {
      await deleteSession(id);
      await load();
    } catch (err) {
      const detail =
        String(err.message || '')
          .match(/"detail":"([^"]+)"/)?.[1]
        || err.message;

      alert(`Не удалось удалить аккаунт: ${detail}`);
    }
  };

  const handleProxyChange = async (sessionId, proxyId) => {
    const loadingKey = `proxy:${sessionId}`;

    setLoading((previous) => ({
      ...previous,
      [loadingKey]: true,
    }));

    try {
      await setSessionProxy(
        sessionId,
        proxyId || null,
      );
      await load();
    } catch (err) {
      alert(`Proxy assignment failed: ${err.message}`);
    }

    setLoading((previous) => ({
      ...previous,
      [loadingKey]: false,
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();

    const messengerType =
      form.messenger_type;

    try {
      const result =
        await createSession(form);

      setCreateResult({
        session_id: result.session_id,
      });

      const createdSession = {
        id: result.session_id,
        messenger_type:
          messengerType,
        phone_number:
          form.phone_number || '',
        auth_state:
          'AUTHORIZING',
      };

      setQrData(null);
      setQrSession(
        createdSession
      );

      setShowCreate(false);

      // Normal onboarding is entirely browser-driven:
      // authenticated cookie + CSRF -> backend launcher.
      await startSession(
        result.session_id
      );

      setForm({
        phone_number: '',
        messenger_type: 'telegram',
      });

      await load();
    } catch (err) {
      alert(
        'Ошибка: ' + err.message
      );
    }
  };

  return (
    <>
      <div className="mf-subpage-heading">
        <div>
          <h2>Личные аккаунты</h2>
          <span>
            Личные аккаунты Telegram · MAX · WhatsApp
          </span>
        </div>
      </div>
      <p style={{ color: '#718096', marginBottom: 24 }}>
        Подключайте рабочие аккаунты мессенджеров. Состояние соединения обновляется автоматически.
      </p>

        <div
          className="sv-management-note"
          style={{ marginBottom: 24 }}
        >
          Новые личные аккаунты подключаются через мастер выше.
          Здесь можно управлять уже созданными подключениями
          и продолжить авторизацию.
        </div>

      {showCreate && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 16 }}>Подключить личный аккаунт</h3>
          <form onSubmit={handleCreate}>
            <div className="form-group">
              <label>
                {form.messenger_type === 'max'
                  ? 'Личный аккаунт MAX'
                  : 'Номер телефона'}
              </label>
              <input
                type="text"
                placeholder={
                  form.messenger_type === 'max'
                    ? 'Номер будет запрошен после запуска подключения'
                    : '+79001234567'
                }
                value={form.phone_number}
                onChange={(e) => setForm({
                  ...form,
                  phone_number: e.target.value,
                })}
                required={form.messenger_type !== 'max'}
                disabled={form.messenger_type === 'max'}
              />
            </div>
            <div className="form-group">
              <label>Мессенджер</label>
              <select
                value={form.messenger_type}
                onChange={(e) => {
                  const messengerType =
                    e.target.value;

                  setForm({
                    ...form,
                    messenger_type:
                      messengerType,
                    phone_number:
                      messengerType === 'max'
                        ? ''
                        : form.phone_number,
                  });
                }}
              >
                <option value="telegram">Telegram</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="max">MAX</option>
              </select>
            </div>
            <button type="submit" className="btn-primary">Подключить аккаунт</button>
          </form>

          {createResult && (
            <div style={{ marginTop: 16, padding: 16, background: '#f0fff4', borderRadius: 8, border: '1px solid #c6f6d5' }}>
              <strong>Аккаунт создан</strong>
              <p style={{ margin: '8px 0 4px', fontSize: 14 }}>Сессия: <code>{createResult.session_id}</code></p>
              <p style={{ margin: '4px 0', fontSize: 14 }}>
                Подключение запущено. Завершите авторизацию в открывшемся окне.
              </p>
            </div>
          )}
        </div>
      )}

      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Аккаунт</th>
              <th>Мессенджер</th>
              <th>Прокси</th>
              <th>Состояние</th>
              <th>Переподключения</th>
              <th>Ошибка</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {sessions.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: 'center', color: '#a0aec0' }}>Подключённых личных аккаунтов пока нет</td></tr>
            )}
            {sessions.map((s) => (
              <tr key={s.id}>
                <td><strong>{s.phone_number || 'Не указан'}</strong></td>
                <td>{messengerTypeLabel(s.messenger_type)}</td>
                <td>
                  <select
                    value={s.proxy_id || ''}
                    disabled={Boolean(loading[`proxy:${s.id}`])}
                    onChange={(event) => handleProxyChange(
                      s.id,
                      event.target.value,
                    )}
                    title="Смена прокси применится при следующем запуске подключения"
                  >
                    <option
                      value=""
                      disabled={s.messenger_type === 'telegram'}
                    >
                      {s.messenger_type === 'telegram'
                        ? 'MTProxy обязателен'
                        : 'Прямое подключение'}
                    </option>
                    {proxies.map((proxy) => (
                      <option
                        key={proxy.id}
                        value={proxy.id}
                        disabled={
                          !proxy.is_healthy
                            || (
                              s.messenger_type === 'telegram'
                              && proxy.proxy_type !== 'mtproto'
                            )
                          || (
                            proxy.proxy_type === 'mtproto'
                            && s.messenger_type !== 'telegram'
                          )
                        }
                      >
                        {proxy.proxy_type}://{proxy.host}:{proxy.port}
                        {proxy.has_auth
                          ? (
                            proxy.proxy_type === 'mtproto'
                              ? ' (защищено)'
                              : ' (с авторизацией)'
                          )
                          : ''}
                        {!proxy.is_healthy ? ' [недоступен]' : ''}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <WorkerStatusBadge
                    workerStatus={s.worker_status}
                    authRequest={s.auth_request}
                    authState={s.auth_state}
                    sessionStatus={s.status}
                    needsReauth={s.needs_reauth}
                    restarting={s.restarting}
                  />
                </td>
                <td>{s.reconnect_attempts}</td>
                <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, color: '#e53e3e' }}>
                  {s.last_error || '-'}
                </td>
                <td>
                  {pendingDelete === s.id ? (
                    <div className="btn-group">
                      <span style={{ fontSize: 13, color: '#742a2a', marginRight: 8 }}>
                        Удалить аккаунт? История чатов сохранится.
                      </span>
                      <button className="btn-danger btn-sm" onClick={() => handleDelete(s.id)}>
                        Да, удалить
                      </button>
                      <button className="btn-ghost btn-sm" onClick={() => setPendingDelete(null)}>
                        Отмена
                      </button>
                    </div>
                  ) : (
                    <div className="btn-group">
                      {/* Auth: shown when the worker is actively waiting
                          for operator input (MAX SMS code is the
                          canonical case — TG also lands here for 2FA
                          password). Opens the same modal as Reauth but
                          skips the QR request because we already have a
                          code/password prompt pending. */}
                      {sessionNeedsAuthAttention(s) && (
                        <button
                          className="btn-primary btn-sm"
                          onClick={() => (
                            s.messenger_type === 'whatsapp'
                              ? handleQR(s)
                              : setQrSession(s)
                          )}
                          title="Требуется ввод данных — открыть форму"
                        >Продолжить</button>
                      )}
                      {/* WhatsApp QR: open the already-generated QR without requesting a new auth cycle. */}
                      {s.messenger_type === 'whatsapp'
                        && s.worker_status === 'starting'
                        && !s.auth_request && (
                        <button
                          className="btn-primary btn-sm"
                          onClick={() => handleQR(s)}
                          disabled={Boolean(loading[s.id])}
                          title="Запросить свежий QR-код WhatsApp"
                        >
                          QR
                        </button>
                      )}
                      {/* Reauth: only shown when auto-recovery is impossible
                          (banned / error). For healthy / transient states the
                          session reconnects on its own — Reauth would destroy
                          a working session. */}
                      {sessionCanReauth(s) && (
                        <button
                          className="btn-primary btn-sm"
                          onClick={() => handleReauth(s)}
                          disabled={loading[s.id]}
                          title="Войти заново: текущая сессия будет сброшена; отсканируйте QR-код или введите SMS-код"
                        >
                          {loading[s.id] ? '...' : 'Войти заново'}
                        </button>
                      )}
                      {sessionCanConnect(s) ? (
                        <button
                          className="btn-success btn-sm"
                          onClick={() => handleConnect(s.id)}
                          title="Запустить подключение"
                        >
                          Подключить
                        </button>
                      ) : (
                        <>
                          <button
                            className="btn-ghost btn-sm"
                            onClick={() => handleRestart(s)}
                            disabled={Boolean(loading[s.id])}
                            title="Перезапустить подключение с сохранённой авторизацией"
                          >
                            {loading[s.id] ? '...' : 'Перезапустить'}
                          </button>
                          <button
                            className="btn-danger btn-sm"
                            onClick={() => handleDisconnect(s.id)}
                            disabled={s.worker_status === 'disconnected'}
                            title="Остановить подключение — данные авторизации сохранятся"
                          >
                            Отключить
                          </button>
                        </>
                      )}
                      {/* Account delete removes authorization/runtime but keeps the
                          archived channel and every existing chat/message. */}
                      <button
                        className="btn-danger btn-sm"
                        onClick={() => setPendingDelete(s.id)}
                        title="Удалить аккаунт и авторизацию. История чатов и сообщений сохранится."
                      >🗑</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {qrSession && (
        <QRModal
          session={
            sessions.find(
              (item) =>
                item.id === qrSession.id
            )
            || qrSession
          }
          data={qrData}
          onClose={() => setQrSession(null)}
        />
      )}
    </>
  );
}

// ── QR / Re-auth modal ──
function QRModal({ session, data, onClose }) {
  const isWA = session.messenger_type === 'whatsapp';
  const isTelegram =
    session.messenger_type === 'telegram';
  const isMax =
    session.messenger_type === 'max';

  const authCapabilities =
    messengerAuthCapabilities(
      session.messenger_type
    );

  const supportsQR =
    authCapabilities.qr;

  const qr = data?.qr;
  const pairingCode = data?.pairing_code;
  const authRequest = data?.auth_request;
  const authRequestData =
    data?.auth_request_data || {};

  const stage = authStageForSession(
    session,
    data,
  );

  return (
    <div
      className="mf-onboarding-overlay"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      <div
        className="mf-onboarding-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 12, padding: 28, minWidth: 360,
          maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>Подключение аккаунта</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#718096' }}>
            ×
          </button>
        </div>
        <div
          style={{
            color: '#718096',
            fontSize: 14,
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {isMax && (
            <img
              src="/brand/messengers/max-personal.svg"
              alt="MAX"
              width="24"
              height="24"
              style={{
                display: 'block',
                flex: '0 0 auto',
              }}
            />
          )}
          <span>
            {isMax ? 'MAX' : session.messenger_type}
            {session.phone_number
              ? ` · ${session.phone_number}`
              : (
                supportsQR
                  ? ' · QR'
                  : ''
              )}
          </span>
        </div>

        {isWA && qr && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ background: '#fff', padding: 12, display: 'inline-block', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <QRCodeSVG value={qr} size={260} level="M" />
            </div>
            <p style={{ marginTop: 16, fontSize: 13, color: '#4a5568' }}>
              WhatsApp → Настройки → Связанные устройства → Привязать устройство
            </p>
          </div>
        )}

        {isWA && !qr && pairingCode && (
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: 14, color: '#4a5568', marginBottom: 12 }}>Код подключения:</p>
            <code style={{ display: 'block', fontSize: 32, padding: '12px 20px', background: '#f7fafc', borderRadius: 8, letterSpacing: 4 }}>
              {pairingCode}
            </code>
          </div>
        )}

        {isTelegram && qr && (
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                background: '#fff',
                padding: 12,
                display: 'inline-block',
                borderRadius: 8,
                border: '1px solid #e2e8f0',
              }}
            >
              <QRCodeSVG
                value={qr}
                size={260}
                level="M"
              />
            </div>

            <p
              style={{
                marginTop: 16,
                fontSize: 13,
                color: '#4a5568',
              }}
            >
              Telegram → Настройки → Устройства → Подключить устройство
            </p>

            <p
              style={{
                marginTop: 6,
                fontSize: 12,
                color: '#718096',
              }}
            >
              QR автоматически обновится после истечения.
            </p>
          </div>
        )}

        {!isWA && (
          stage === 'password_requested'
          || !(isTelegram && qr)
        ) && (
          <AuthForm
            session={session}
            authRequest={stage}
            authRequestData={authRequestData}
            onRequestQR={
              isTelegram
              && supportsQR
                ? () => requestQR(session.id)
                : null
            }
          />
        )}

        {isWA && !qr && !pairingCode && (
          <div style={{ textAlign: 'center', color: '#718096', padding: 40 }}>
            <div style={{ marginBottom: 12 }}>Готовим QR-код…</div>
            <div style={{ fontSize: 12 }}>(подключение может занять от 5 сек.)</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Interactive phone/code/password form for TG/MAX ──
function AuthForm({
  session,
  authRequest,
  authRequestData,
  onRequestQR,
}) {
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const stage = authRequest;
  const durableState =
    normalizeAuthState(
      session.auth_state
    );

  // Clear input when stage changes
  useEffect(() => { setValue(''); setError(null); }, [stage]);

  useEffect(() => {
    if (
      stage !== 'code_requested'
      || session.messenger_type !== 'max'
    ) {
      return undefined;
    }

    setNowMs(Date.now());

    const timer = setInterval(
      () => setNowMs(Date.now()),
      1000,
    );

    return () => clearInterval(timer);
  }, [
    stage,
    session.messenger_type,
    authRequestData?.resend_available_at,
  ]);

  // TG_AUTH_EFFECTIVE_PHONE_V1
  const effectiveAuthValue = (
    stage === 'phone_requested'
    && !String(value || '').trim()
      ? String(session.phone_number || '')
      : String(value || '')
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!effectiveAuthValue.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      if (stage === 'phone_requested') await providePhone(session.id, effectiveAuthValue.trim());
      else if (
        stage === 'code_requested'
        || stage === 'email_code_requested'
      ) {
        await provideCode(
          session.id,
          effectiveAuthValue.trim(),
        );
      }
      else if (stage === 'password_requested') await providePassword(session.id, effectiveAuthValue.trim());
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
    setValue('');
  };

  const handleRequestQR = async () => {
    if (!onRequestQR) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await onRequestQR();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resendAvailableAt =
    Number(
      authRequestData
        ?.resend_available_at
      || 0
    );

  const resendWaitMs =
    Math.max(
      0,
      resendAvailableAt
      - nowMs,
    );

  const resendWaitSeconds =
    Math.ceil(
      resendWaitMs / 1000
    );

  const handleResendCode = async () => {
    if (
      stage !== 'code_requested'
      || session.messenger_type !== 'max'
      || submitting
      || resendWaitMs > 0
    ) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await resendCode(
        session.id
      );
    } catch (err) {
      setError(
        err.message
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (
    durableState
    === 'wait_device_confirmation'
  ) {
    return (
      <div
        style={{
          textAlign: 'center',
          color: '#4a5568',
          padding: 32,
        }}
      >
        <strong>
          Подтвердите вход на уже авторизованном устройстве
        </strong>

        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            color: '#718096',
          }}
        >
          После подтверждения состояние обновится автоматически.
        </div>
      </div>
    );
  }

  if (durableState === 'failed') {
    return (
      <div
        style={{
          background: '#fff5f5',
          color: '#c53030',
          padding: 16,
          borderRadius: 8,
        }}
      >
        {
          session.last_error
          || 'Авторизация завершилась ошибкой'
        }
      </div>
    );
  }

  if (durableState === 'authorized') {
    return (
      <div
        style={{
          textAlign: 'center',
          color: '#276749',
          padding: 32,
        }}
      >
        Аккаунт авторизован.
      </div>
    );
  }

  if (!stage) {
    return (
      <div style={{ textAlign: 'center', color: '#718096', padding: 40 }}>
        <div style={{ marginBottom: 12 }}>Ожидаем запуск подключения…</div>
        <div style={{ fontSize: 12 }}>(перезапуск может занять от 5 сек.)</div>
      </div>
    );
  }

  // Help text differs per messenger — TG sends code via app first then
  // SMS; MAX sends only SMS; WA doesn't use this form at all.
  const telegramDelivery =
    authRequestData?.delivery;

  const codeHelp = session.messenger_type === 'max'
    ? `SMS-код для ${session.phone_number || 'указанного номера'}`
    : (
      telegramDelivery === 'telegram_app'
        ? 'Код отправлен в служебный чат Telegram на другом авторизованном устройстве'
        : `Telegram запросил код для ${session.phone_number}; фактический способ доставки пока не определён`
    );
  const config = {
    phone_requested: {
      label: 'Номер телефона',
      placeholder: '+79001234567',
      help: 'Номер с кодом страны',
      type: 'tel',
    },
    code_requested: {
      label: 'Код входа',
      placeholder: '12345',
      help: codeHelp,
      type: 'text',
    },
    email_code_requested: {
      label: 'Код из почты',
      placeholder: '123456',
      help: 'Введите код подтверждения, полученный по электронной почте',
      type: 'text',
    },
    password_requested: {
      label: 'Пароль 2FA',
      placeholder: '••••••••',
      help: 'Облачный пароль, если для аккаунта включена 2FA',
      type: 'password',
    },
  }[stage] || {};

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500 }}>
          {config.label}
        </label>
        <input
          type={config.type}
          value={effectiveAuthValue}
          onChange={(e) => setValue(e.target.value)}
          placeholder={config.placeholder}
          autoFocus
          disabled={submitting}
          style={{ width: '100%', padding: '10px 12px', fontSize: 15, border: '1px solid #e2e8f0', borderRadius: 6 }}
        />
        <p style={{ fontSize: 12, color: '#718096', margin: '6px 0 0' }}>{config.help}</p>
      </div>

      {stage === 'phone_requested' && onRequestQR && (
        <button
          type="button"
          className="btn-ghost"
          onClick={handleRequestQR}
          disabled={submitting}
          style={{
            width: '100%',
            marginBottom: 12,
          }}
        >
          Войти по QR-коду
        </button>
      )}

      {stage === 'code_requested'
        && session.messenger_type === 'max' && (
        <div style={{ marginBottom: 12 }}>
          <button
            type="button"
            className="btn-ghost"
            onClick={handleResendCode}
            disabled={
              submitting
              || resendWaitMs > 0
            }
            style={{ width: '100%' }}
          >
            {
              resendWaitSeconds > 0
                ? `Отправить код повторно через ${resendWaitSeconds} сек.`
                : 'Отправить код повторно'
            }
          </button>

          {authRequestData?.request_count_left != null && (
            <div
              style={{
                marginTop: 6,
                fontSize: 12,
                color: '#718096',
                textAlign: 'center',
              }}
            >
              Осталось запросов кода: {authRequestData.request_count_left}
            </div>
          )}

          {authRequestData?.resend_error && (
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                color: '#c53030',
              }}
            >
              {authRequestData.resend_error}
            </div>
          )}
        </div>
      )}

      {error && (
        <div style={{ background: '#fff5f5', color: '#c53030', padding: 10, borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
          {error}
        </div>
      )}

      <button
        type="submit"
        className="btn-primary"
        disabled={submitting || !effectiveAuthValue.trim()}
        style={{ width: '100%' }}
      >
        {submitting ? 'Отправляем…' : 'Продолжить'}
      </button>
    </form>
  );
}
